import React, { useEffect, useState } from 'react';
import save_card from '../assets/save_card.png';
import { cn } from '../lib/utils';
import { importantNotesSteps, ImportantNoteStep } from './importantNotesSteps';
import RedirectSpinner from './RedirectSpinner';

interface ImportantNotesContentProps {
  steps?: ImportantNoteStep[];
  onComplete: () => void; // invoked after final step when spinner shows
  extraContentRender?: (stepIndex: number) => React.ReactNode; // for images etc.
  spinnerMessage?: string;
  spinnerSubMessage?: string;
  className?: string;
}

export const ImportantNotesContent: React.FC<ImportantNotesContentProps> = ({
  steps = importantNotesSteps,
  onComplete,
  extraContentRender,
  spinnerMessage,
  spinnerSubMessage,
  className = ''
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    // reset when steps prop length changes (if dynamic)
    setCurrentStep(0);
    setIsRedirecting(false);
  }, [steps.length]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      setIsRedirecting(true);
      onComplete();
    }
  };

  if (isRedirecting) {
    return (
      <RedirectSpinner
        message={spinnerMessage}
        subMessage={spinnerSubMessage}
      />
    );
  }

  const current = steps[currentStep];

  return (
    <div className={cn('pb-2 pt-4', className)}>
      {/* Progress */}
      <div className="mb-6 flex items-center justify-center">
        <div
          className={cn(
            'flex items-center justify-center rounded-full transition-all duration-500 ease-in-out',
            currentStep === 0
              ? 'h-5 w-5 bg-orange-500 ring-2 ring-orange-300 ring-offset-2'
              : currentStep === 1
                ? 'h-6 w-6 bg-green-500'
                : 'bg-gray-300'
          )}
        >
          {currentStep >= 1 && (
            <span className="text-sm text-white transition-opacity duration-300">
              ✓
            </span>
          )}
        </div>
        <div
          className={cn(
            'mx-3 h-0.5 w-12 transition-colors duration-500 ease-in-out',
            currentStep >= 1 ? 'bg-orange-500' : 'bg-gray-300'
          )}
        />
        <div
          className={cn(
            'flex h-5 w-5 items-center justify-center rounded-full transition-all duration-500 ease-in-out',
            currentStep === 1
              ? 'bg-orange-500 ring-2 ring-orange-300 ring-offset-2'
              : 'bg-gray-300'
          )}
        />
      </div>

      <h2 className="flex items-center justify-center text-center text-base font-medium">
        {current.title}
      </h2>
      <div className="mt-6 text-center text-sm leading-6">
        <p className="text-gray-600">{current.content}</p>
        {extraContentRender ? (
          extraContentRender(currentStep)
        ) : currentStep === 1 ? (
          <div className="mt-3 flex justify-center">
            <img
              className="mt-1"
              src={save_card}
              alt="Save Card"
              width={265}
              height={50}
            />
          </div>
        ) : null}
      </div>

      <div className="mt-8 flex justify-center pb-1">
        <button
          onClick={handleNext}
          className="rounded-full bg-[#5CB891] px-16 py-2 text-white transition-colors hover:bg-[#4aa07c]"
        >
          OK
        </button>
      </div>
    </div>
  );
};
